import settings from '@src/conf';

import { isNil } from '@src/utils';
import { useCallback, useMemo, useState } from 'react';

const AUTH_STORAGE_KEY = 'ReactStorage__Authentication';

function useJwtStorage() {
  const parse = () => {
    const value = localStorage.getItem(AUTH_STORAGE_KEY);
    return value ? JSON.parse(value) : null;
  };

  const [credentials, setCredentials] = useState(parse);

  const isAccessTokenExpired = useMemo(() => {
    const expiredAt = credentials?.metadata?.accessTokenExpiredAt;
    if (isNil(expiredAt)) {
      return true;
    }
    const delta = expiredAt - Date.now();
    return delta < settings.AUTH.JWT.EXPIRED_BUFFER;
  }, [credentials]);

  const isRefreshTokenExpired = useMemo(() => {
    const expiredAt = credentials?.metadata?.refreshTokenExpiredAt;
    if (isNil(expiredAt)) {
      return true;
    }
    const delta = expiredAt - Date.now();
    return delta < settings.AUTH.JWT.EXPIRED_BUFFER;
  }, [credentials]);

  const isExpired = useMemo(
    () => isAccessTokenExpired || isRefreshTokenExpired,
    [isAccessTokenExpired, isRefreshTokenExpired],
  );
  const isAuthenticated = useMemo(
    () => !isExpired && Boolean(credentials),
    [credentials, isExpired],
  );

  const set = useCallback((payload: object) => {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
    setCredentials(payload);
  }, []);

  const remove = useCallback(() => {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(null));
    setCredentials(null);
  }, []);

  return {
    set,
    remove,
    parse,
    credentials,
    isAuthenticated,
  };
}

export { AUTH_STORAGE_KEY, useJwtStorage };
